import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { toast } from "react-toastify";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import {
  getCompanyUploadedFileSignedUrl,
  getPaymentPlatformImport,
  getPaymentPlatformOverview,
  previewPaymentPlatformImport,
  reprocessPaymentPlatformImport,
  savePaymentPlatformColumnMapping,
  startPaymentPlatformImport,
  searchPaymentPlatformEntities,
  updatePaymentPlatformModel,
} from "./api/companyApi";
import { sendClaudeChat } from "./api/claudeApi";
import PaymentPlatformDecisionStudio from "./components/PaymentPlatformDecisionStudio";
import PaymentPlatformExecutiveTable from "./components/PaymentPlatformExecutiveTable";
import PaymentPlatformAddCompanyFlow from "./components/PaymentPlatformAddCompanyFlow";
import PaymentPlatformColumnMappingDialog from "./components/PaymentPlatformColumnMappingDialog";
import PaymentPlatformModelLab from "./components/PaymentPlatformModelLab";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#12a150",
  amber: "#d97706",
  red: "#dc2626",
};

const fmt = (value) => (value == null ? "—" : Number(value).toLocaleString());
const money = (value) =>
  value == null ? "—" : `$${Number(value).toLocaleString()}`;
const decisionColor = (value) =>
  value === "approve"
    ? C.green
    : value === "review"
      ? C.amber
      : value === "decline"
        ? C.red
        : C.muted;

function Kpi({ label, value, sub }) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderColor: C.line, borderRadius: 3 }}
    >
      <Typography
        sx={{
          fontSize: 11,
          color: C.muted,
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 25, color: C.ink, fontWeight: 900 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: C.muted }}>{sub}</Typography>
      )}
    </Card>
  );
}

function AssessmentResult({ assessment }) {
  if (!assessment) return null;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: C.line, borderRadius: 3, mb: 2 }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography sx={{ fontWeight: 900, color: C.ink }}>
          New recommendation
        </Typography>
        <Chip
          size="small"
          label={assessment.assessment?.decision?.replaceAll("_", " ")}
          sx={{
            color: decisionColor(assessment.assessment?.decision),
            fontWeight: 800,
          }}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <Kpi label="Score" value={assessment.assessment?.score ?? "—"} />
        <Kpi
          label="Evidence coverage"
          value={`${assessment.assessment?.coverage || 0}%`}
        />
        <Kpi
          label="Recommended amount"
          value={money(assessment.assessment?.recommendation?.amount)}
        />
        <Kpi
          label="Term"
          value={`${assessment.assessment?.recommendation?.termMonths || "—"} months`}
        />
      </Box>
    </Paper>
  );
}

export default function PaymentPlatformAssessmentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const tenantId = isAdmin ? getTenantIdFromURL() : currentTenantId;
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [mappingBusy, setMappingBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newAssessment, setNewAssessment] = useState(null);
  const [modelDraft, setModelDraft] = useState(null);
  const [savingModel, setSavingModel] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("addCompany") !== "1") return;
    setAddOpen(true);
    params.delete("addCompany");
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const [chatBusy, setChatBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [entityResult, setEntityResult] = useState({
    items: [],
    nextCursor: null,
    total: null,
  });
  const [entityLoading, setEntityLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [coverageFilter, setCoverageFilter] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [entityPage, setEntityPage] = useState(0);
  const [entityCursors, setEntityCursors] = useState([null]);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getPaymentPlatformOverview(tenantId);
      setOverview(data);
      setModelDraft(data?.model || null);
      setJob(data?.latestImport || null);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Could not load payment-platform assessments",
      );
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setEntityPage(0);
    setEntityCursors([null]);
  }, [debouncedSearch, decisionFilter, riskFilter, coverageFilter, pageSize]);
  useEffect(() => {
    if (!tenantId || tab !== 0) return undefined;
    let active = true;
    setEntityLoading(true);
    searchPaymentPlatformEntities(tenantId, {
      query: debouncedSearch || undefined,
      decision: decisionFilter || undefined,
      risk: riskFilter || undefined,
      minCoverage: coverageFilter || undefined,
      pageSize,
      cursor: entityCursors[entityPage] || undefined,
    })
      .then((data) => {
        if (active)
          setEntityResult(data || { items: [], nextCursor: null, total: null });
      })
      .catch((error) => {
        if (active)
          toast.error(
            error?.response?.data?.message || "Could not search companies",
          );
      })
      .finally(() => {
        if (active) setEntityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    tenantId,
    tab,
    debouncedSearch,
    decisionFilter,
    riskFilter,
    coverageFilter,
    pageSize,
    entityPage,
    entityCursors,
  ]);
  useEffect(() => {
    if (!job?.id || !["queued", "processing"].includes(job.status))
      return undefined;
    const timer = setInterval(async () => {
      try {
        const latest = await getPaymentPlatformImport(tenantId, job.id);
        setJob(latest);
        if (latest.status === "completed") {
          toast.success("Entity matching and MCA / loan assessments completed");
          await load();
        }
        if (latest.status === "failed")
          toast.error(latest.error || "Import failed");
      } catch (error) {
        /* retry on next poll */
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [job?.id, job?.status, tenantId, load]);

  const uploadFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of selected) {
        const signed = await getCompanyUploadedFileSignedUrl(tenantId, {
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
        });
        await axios.put(signed.data.uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        uploaded.push({
          fileGCSKey: signed.data.fileGCSKey,
          fileName: signed.data.originalFileName || file.name,
        });
      }
      const preview = await previewPaymentPlatformImport(tenantId, uploaded);
      setPendingUpload({ files: uploaded, preview });
      toast.success(`${uploaded.length} files inspected. Map the model inputs and outcome.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "File upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startUploadedImport = async (columnMapping) => {
    if (!pendingUpload?.files?.length) return;
    setUploading(true);
    try {
      const started = await startPaymentPlatformImport(
        tenantId,
        pendingUpload.files,
        null,
        columnMapping,
      );
      setJob(started);
      setPendingUpload(null);
      toast.success("Column mapping saved. Assessments are rebuilding with the uploaded data.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not start import");
    } finally {
      setUploading(false);
    }
  };

  const openColumnMapping = async () => {
    if (!job?.id || !job?.files?.length) {
      toast.error("The source files for this import are not available.");
      return;
    }
    setMappingBusy(true);
    try {
      const preview = await previewPaymentPlatformImport(tenantId, job.files);
      setMappingPreview(preview);
      setMappingOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not inspect import columns");
    } finally {
      setMappingBusy(false);
    }
  };

  const applyExistingColumnMapping = async (columnMapping) => {
    if (!job?.id) return;
    setMappingBusy(true);
    try {
      const started = await reprocessPaymentPlatformImport(tenantId, job.id, columnMapping);
      setJob(started);
      setMappingOpen(false);
      toast.success("Column mapping saved. Assessments are rebuilding from the existing files.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not apply column mapping");
    } finally {
      setMappingBusy(false);
    }
  };

  const saveColumnMappingDraft = async (columnMapping) => {
    if (!job?.id) return;
    setMappingBusy(true);
    try {
      const saved = await savePaymentPlatformColumnMapping(tenantId, job.id, columnMapping);
      setJob((current) => ({ ...current, columnMappingDraft: saved }));
      setModelDraft((current) => ({ ...current, trainingColumnMappingDraft: saved }));
      setMappingOpen(false);
      toast.success("Column mapping saved. You can complete the outcome label and rebuild later.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save column mapping");
    } finally {
      setMappingBusy(false);
    }
  };

  const progress = job?.matchedEntities
    ? Math.round(((job.assessedEntities || 0) / job.matchedEntities) * 100)
    : job?.filesRead
      ? Math.round((job.filesRead / job.totalFiles) * 25)
      : 0;
  const activeColumnMapping =
    job?.columnMappingDraft ||
    modelDraft?.trainingColumnMappingDraft ||
    job?.columnMapping ||
    modelDraft?.trainingColumnMapping ||
    null;
  const mappedFieldCount = Object.values(activeColumnMapping?.fields || {}).filter(Boolean).length;
  const mappingIsProcessing = ["queued", "processing"].includes(job?.status);
  const openCompany = (entityId) =>
    navigate(
      isAdmin
        ? `/tenant-dashboard/${tenantId}/assessments/company/${entityId}`
        : `/assessments/company/${entityId}`,
    );
  const nextEntityPage = () => {
    if (!entityResult.nextCursor) return;
    setEntityCursors((current) => {
      const next = current.slice(0, entityPage + 1);
      next[entityPage + 1] = entityResult.nextCursor;
      return next;
    });
    setEntityPage((current) => current + 1);
  };
  const updateModel = (section, key, value) =>
    setModelDraft((current) => ({
      ...current,
      [section]: { ...(current?.[section] || {}), [key]: value },
    }));
  const saveModel = async (patch = modelDraft) => {
    setSavingModel(true);
    try {
      const saved = await updatePaymentPlatformModel(tenantId, patch);
      setModelDraft(saved);
      setProposal(null);
      toast.success(
        `Model v${saved.version} saved for ${overview?.tenant?.name}`,
      );
      return saved;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save model");
      return null;
    } finally {
      setSavingModel(false);
    }
  };

  const chatContext = useMemo(
    () => ({
      tenant: overview?.tenant,
      model: modelDraft,
      dataset: job
        ? {
            profiles: job.profiles,
            outcomes: job.outcomes,
            validation: job.validation,
            matchedEntities: job.matchedEntities,
            decisionDistribution: job.decisionDistribution,
          }
        : null,
    }),
    [overview?.tenant, modelDraft, job],
  );
  const askModel = async () => {
    if (!chatInput.trim()) return;
    const nextMessages = [
      ...messages,
      { role: "user", content: chatInput.trim() },
    ];
    setMessages(nextMessages);
    setChatInput("");
    setChatBusy(true);
    try {
      const response = await sendClaudeChat({
        tenantName: overview?.tenant?.name || "Payment platform",
        entityName: "MCA / loan model configuration",
        entity: {
          ...chatContext,
          paymentPlatformModelTuning: true,
          tenantId,
          instruction:
            'Analyze only schema, coverage, statistics, measured experiments and the draft policy. Never claim the model is validated when outcomes are one-sided. Changes remain proposals until the user explicitly applies them.',
        },
        messages: nextMessages,
        allowWeb: false,
      });
      const reply =
        response?.data?.reply ||
        response?.reply ||
        response?.data ||
        "No response";
      setMessages((current) => [
        ...current,
        { role: "assistant", content: String(reply) },
      ]);
      const match = String(reply).match(/\{\s*"modelPatch"[\s\S]*\}/);
      if (match) {
        try {
          setProposal(JSON.parse(match[0]).modelPatch || null);
        } catch (error) {
          /* prose-only response */
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "ArcAI could not answer");
    } finally {
      setChatBusy(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (!overview)
    return (
      <Alert severity="warning">
        Payment-platform assessment workspace is unavailable.
      </Alert>
    );
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1450, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          alignItems: "flex-start",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 950, color: C.ink }}>
            MCA / Loan Assessments
          </Typography>
          <Typography sx={{ color: C.muted }}>
            Sparse-identity merchant matching and a model isolated to{" "}
            {overview.tenant?.name} data.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add company
          </Button>
        </Box>
      </Box>
      <Alert
        severity={
          modelDraft?.validation?.status === "ready_for_backtest"
            ? "success"
            : "warning"
        }
        sx={{ mb: 2 }}
      >
        <strong>Model validation:</strong>{" "}
        {modelDraft?.validation?.status?.replaceAll("_", " ")} —{" "}
        {modelDraft?.validation?.reason}
      </Alert>
      <AssessmentResult assessment={newAssessment} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Kpi
          label="Matched merchants"
          value={fmt(job?.matchedEntities || 0)}
          sub={`${fmt(job?.unmatchedRows || 0)} require identity review`}
        />
        <Kpi
          label={isAdmin ? "Files in latest run" : "Assessed merchants"}
          value={fmt(isAdmin ? (job?.totalFiles || job?.files?.length || 0) : (job?.assessedEntities || 0))}
          sub={isAdmin ? (job?.status || "No import") : "Current decision population"}
        />
        <Kpi
          label="Approve / review"
          value={`${fmt(job?.decisionDistribution?.approve || 0)} / ${fmt(job?.decisionDistribution?.review || 0)}`}
          sub="Policy recommendations"
        />
        <Kpi
          label="Model version"
          value={`v${modelDraft?.version || 1}`}
          sub={modelDraft?.status || "draft"}
        />
      </Box>
      <Paper variant="outlined" sx={{ borderColor: C.line, borderRadius: 3 }}>
        <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ px: 2 }}>
          <Tab label="Assessments" />
          <Tab label="Decision studio" />
          <Tab label={isAdmin ? "Data & matching" : "Data quality"} />
          <Tab label="Model Lab" />
          <Tab label="ArcAI model chat" />
        </Tabs>
        <Divider />
        {tab === 0 && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mb: 2 }}>
              <TextField
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, merchant ID, EIN, email, website or application…"
                sx={{ flex: "1 1 390px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Decision"
                value={decisionFilter}
                onChange={(event) => setDecisionFilter(event.target.value)}
                sx={{ minWidth: 145 }}
              >
                <MenuItem value="">All decisions</MenuItem>
                <MenuItem value="approve">Approve</MenuItem>
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="decline">Decline</MenuItem>
                <MenuItem value="insufficient_data">Insufficient data</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Risk"
                value={riskFilter}
                onChange={(event) => setRiskFilter(event.target.value)}
                sx={{ minWidth: 125 }}
              >
                <MenuItem value="">All risk</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Evidence"
                value={coverageFilter}
                onChange={(event) => setCoverageFilter(event.target.value)}
                sx={{ minWidth: 145 }}
              >
                <MenuItem value="">Any coverage</MenuItem>
                <MenuItem value="25">25%+</MenuItem>
                <MenuItem value="50">50%+</MenuItem>
                <MenuItem value="75">75%+</MenuItem>
              </TextField>
            </Box>
            {entityLoading ? (
              <Box sx={{ py: 7, textAlign: "center" }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <PaymentPlatformExecutiveTable
                rows={entityResult.items || []}
                onOpenCompany={openCompany}
                viewport="assessment-workspace"
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ color: C.muted, fontSize: 13 }}>
                Page {entityPage + 1}
                {entityResult.total != null
                  ? ` · ${fmt(entityResult.total)} companies`
                  : ""}
                {entityResult.searchLimited
                  ? " · first 1,000 search matches"
                  : ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  select
                  size="small"
                  label="Rows"
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                  sx={{ width: 90 }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </TextField>
                <Button
                  variant="outlined"
                  disabled={entityPage === 0 || entityLoading}
                  onClick={() =>
                    setEntityPage((current) => Math.max(0, current - 1))
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  disabled={!entityResult.nextCursor || entityLoading}
                  onClick={nextEntityPage}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        {tab === 1 && modelDraft && (
          <PaymentPlatformDecisionStudio
            tenantId={tenantId}
            modelDraft={modelDraft}
            job={job}
            savingModel={savingModel}
            onUpdateModel={updateModel}
            onSaveModel={saveModel}
            onOpenChat={() => setTab(4)}
          />
        )}
        {tab === 2 && (
          <Box sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, color: C.ink }}>
                  {isAdmin ? "Multi-file entity graph" : "Assessment data quality"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.muted }}>
                  {isAdmin
                    ? "Uploaded datasets are resolved into one merchant assessment population."
                    : "Identity resolution and evidence coverage across the current merchant population."}
                </Typography>
              </Box>
              {isAdmin && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadOutlinedIcon />}
                    disabled={uploading}
                    onClick={() => fileInput.current?.click()}
                  >
                    {uploading ? "Uploading…" : "Upload files together"}
                  </Button>
                  <input
                    hidden
                    multiple
                    ref={fileInput}
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={uploadFiles}
                  />
                </>
              )}
            </Box>
            {job && (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderColor: C.line, mb: 2 }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {job.stage?.replaceAll("_", " ") || job.status}
                  </Typography>
                  <Typography>{progress}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={job.status === "completed" ? 100 : progress}
                  sx={{ mt: 1 }}
                />
                {job.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {job.error}
                  </Alert>
                )}
              </Paper>
            )}
            {isAdmin && job && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: activeColumnMapping ? "#b9dfca" : C.line,
                  bgcolor: activeColumnMapping ? "#f5fcf8" : "#fff",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: C.ink }}>
                      Column mapping & ML readiness
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.25 }}>
                      Map identity, advance size, pricing, model features and the
                      historical outcome. Rebuilding reuses these files.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
                      <Chip
                        size="small"
                        color={mappedFieldCount ? "success" : "default"}
                        label={`${mappedFieldCount} input columns mapped`}
                      />
                      <Chip
                        size="small"
                        color={activeColumnMapping?.label ? "success" : "warning"}
                        label={activeColumnMapping?.label ? "Outcome label mapped" : "Outcome label needed"}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<TuneIcon />}
                    disabled={mappingBusy || mappingIsProcessing || !job?.files?.length}
                    onClick={openColumnMapping}
                  >
                    {mappingBusy ? "Inspecting columns…" : "Configure columns"}
                  </Button>
                </Box>
              </Paper>
            )}
            {isAdmin && (job?.profiles || []).map((profile) => (
              <Paper
                key={`${profile.fileName}-${profile.sheetName}`}
                variant="outlined"
                sx={{ p: 1.5, mb: 1, borderColor: C.line }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>
                      {profile.fileName} · {profile.sheetName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: C.muted }}>
                      {profile.headers?.length} fields · {fmt(profile.rowCount)}{" "}
                      rows · header row {profile.headerRowIndex + 1}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={profile.role?.replaceAll("_", " ")}
                  />
                </Box>
              </Paper>
            ))}
            {!isAdmin && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
                <Kpi label="Resolved merchants" value={fmt(job?.matchedEntities || 0)} sub="Available for assessment" />
                <Kpi label="Identity review" value={fmt(job?.unmatchedRows || 0)} sub="Needs additional verification" />
                <Kpi label="Assessment coverage" value={`${job?.matchedEntities ? Math.round((Number(job?.assessedEntities || 0) / Number(job.matchedEntities)) * 100) : 0}%`} sub="Current population assessed" />
              </Box>
            )}
          </Box>
        )}
        {tab === 3 && modelDraft && (
          <PaymentPlatformModelLab
            tenantId={tenantId}
            modelDraft={modelDraft}
            job={job}
            onOpenPolicy={() => setTab(1)}
            onOpenChat={() => setTab(4)}
            onOpenData={() => setTab(2)}
          />
        )}
        {tab === 4 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateRows: "minmax(360px, 1fr) auto",
              minHeight: 520,
            }}
          >
            <Box sx={{ p: 2.5, overflowY: "auto" }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                ArcAI can analyze coverage, challenge weights and propose a
                model patch. Changes require an explicit Apply action and remain
                isolated to this tenant.
              </Alert>
              {messages.map((message, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1,
                    ml: message.role === "user" ? 8 : 0,
                    mr: message.role === "assistant" ? 8 : 0,
                    bgcolor: message.role === "user" ? "#edf4ff" : "#fff",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 10, fontWeight: 900, color: C.muted }}
                  >
                    {message.role === "user" ? "YOU" : "ARCAI"}
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>
                    {message.content}
                  </Typography>
                </Paper>
              ))}
              {proposal && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      size="small"
                      disabled={savingModel}
                      onClick={() =>
                        saveModel({
                          ...modelDraft,
                          ...proposal,
                          thresholds: {
                            ...modelDraft.thresholds,
                            ...proposal.thresholds,
                          },
                          weights: {
                            ...modelDraft.weights,
                            ...proposal.weights,
                          },
                          guardrails: {
                            ...modelDraft.guardrails,
                            ...proposal.guardrails,
                          },
                          offer: { ...modelDraft.offer, ...proposal.offer },
                        })
                      }
                    >
                      Apply proposal
                    </Button>
                  }
                >
                  ArcAI proposed a configuration change. Review it before
                  applying.
                </Alert>
              )}
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderTop: `1px solid ${C.line}`,
                display: "flex",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Ask how Liberis data should change the MCA / loan policy…"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    askModel();
                  }
                }}
              />
              <IconButton
                onClick={askModel}
                disabled={chatBusy || !chatInput.trim()}
                sx={{
                  bgcolor: C.blue,
                  color: "#fff",
                  "&:hover": { bgcolor: "#1559be" },
                }}
              >
                {chatBusy ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>
      <PaymentPlatformColumnMappingDialog
        open={Boolean(pendingUpload) || mappingOpen}
        preview={pendingUpload?.preview || mappingPreview}
        currentMapping={pendingUpload ? null : activeColumnMapping}
        busy={uploading || mappingBusy}
        onClose={() => {
          if (pendingUpload) setPendingUpload(null);
          setMappingOpen(false);
        }}
        onApply={pendingUpload ? startUploadedImport : applyExistingColumnMapping}
        onSaveDraft={pendingUpload ? null : saveColumnMappingDraft}
      />
      <PaymentPlatformAddCompanyFlow
        tenantId={tenantId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(assessment) => { setNewAssessment(assessment); load(); }}
        onOpenReport={(entityId) => navigate(`/tenant-dashboard/${tenantId}/assessments/company/${entityId}`)}
      />
    </Box>
  );
}
